import React from 'react'
import { Component } from 'reflux'
import { PurchaseOrderStore, PurchaseOrderStoreActions, STATUS } from '../../stores/purchaseOrderStore'
import {
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Typography,
  Box,
  Divider,
  TextField,
  Backdrop,
  Select,
  MenuItem
} from '@material-ui/core'
import CommentSection from '../../components/commentSection'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'

export default class PurchaseOrderReceiveDetails extends Component {
  constructor(props) {
    super(props)
    this.stores = [PurchaseOrderStore]
    this.storeKeys = ['purchaseOrdersToReceiveDetails', 'status', 'purchaseOrderIDs', 'stockLocations', 'productStockLocations']
    this.state = {
      receivedAmounts: {},
      showStockLocationModal: false
    }
  }

  componentDidMount() {
    const purchaseOrderIDs = this.props.location.state?.purchaseOrderIDs || []
    PurchaseOrderStoreActions.fetchMultiplePurchaseOrders(purchaseOrderIDs)
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.purchaseOrdersToReceiveDetails !== prevState.purchaseOrdersToReceiveDetails && !this.state.isSaving) {
      const allLines = this.state.purchaseOrdersToReceiveDetails?.flatMap(po => po.purchaseOrderlines || []) || []
      const grouped = this.groupLinesByVariationAndDescription(allLines)
      const receivedAmounts = {}
      grouped.forEach(group => {
        const groupKey = this.getGroupKey(group[0])
        receivedAmounts[groupKey] = group.reduce((sum, l) => sum + (l.amountReceived || 0), 0)
      })
      this.setState({ receivedAmounts })
    }
  }

  groupLinesByVariationAndDescription(lines) {
    const groups = {}
    lines.forEach(line => {
      const key = this.getGroupKey(line)
      if (!groups[key]) groups[key] = []
      groups[key].push(line)
    })
    return Object.values(groups)
  }

  handleReceivedAmountChange = (groupKey, value) => {
    const parsed = value === '' ? '' : Math.max(0, parseInt(value, 10) || 0)
    this.setState(prev => ({
      receivedAmounts: {
        ...prev.receivedAmounts,
        [groupKey]: parsed
      }
    }))
  }

  openReceiveHandle = () => {
    const allLines = this.state.purchaseOrdersToReceiveDetails?.flatMap(po => po.purchaseOrderlines || []) || []
    this.props.history.push({
      pathname: '/purchase-orders/receive/handle',
      state: { purchaseOrderlines: allLines }
    })
  }

  getGroupKey = line => {
    return `${line.productVariationID}||${line.description}`
  }

  saveRecievedAmount = () => {
    const allLines = this.state.purchaseOrdersToReceiveDetails?.flatMap(po => po.purchaseOrderlines || []) || []
    const grouped = this.groupLinesByVariationAndDescription(allLines)
    const payload = grouped.map(group => {
      const first = group[0]
      const groupKey = this.getGroupKey(first)
      return {
        receivedAmount: Number(this.state.receivedAmounts[groupKey] ?? 0),
        purchaseOrderLineIDs: group.map(l => l.purchaseOrderLineID)
      }
    })
    PurchaseOrderStoreActions.receiveLines(payload)
  }

  reportError = () => {
    const enrichedLines =
      (this.state.purchaseOrdersToReceiveDetails || []).flatMap(po =>
        (po.purchaseOrderlines || []).map(l => ({
          ...l,
          purchaseOrderNumber: po.purchaseOrderNumber,
          PurchaseOrderID: po.PurchaseOrderID
        }))
      ) || []
    const singleQtyLines = enrichedLines.filter(l => Number(l.amountOfProducts || 0) === 1)

    const grouped = this.groupLinesByVariationAndDescription(singleQtyLines)

    const poData = (this.state.purchaseOrdersToReceiveDetails || []).map(po => ({
      purchaseOrderID: po.PurchaseOrderID,
      purchaseOrderNumber: po.purchaseOrderNumber,
      supplierName: po.supplier?.supplierName || po.supplierName
    }))

    const reportData = grouped
      .map(group => {
        const first = group[0]
        const groupKey = this.getGroupKey(first)

        const expectedAmount = group.length

        const receivedAmount =
          this.state.receivedAmounts[groupKey] !== undefined && this.state.receivedAmounts[groupKey] !== ''
            ? Number(this.state.receivedAmounts[groupKey])
            : group.reduce((sum, l) => sum + Number(l.amountReceived || 0), 0)

        const missingAmount = expectedAmount - receivedAmount

        const missingByPOMap = group
          .filter(l => Number(l.amountReceived || 0) < 1)
          .reduce((acc, l) => {
            const key = `${l.PurchaseOrderID}||${l.purchaseOrderNumber}`
            if (!acc[key]) {
              acc[key] = { PurchaseOrderID: l.PurchaseOrderID, purchaseOrderNumber: l.purchaseOrderNumber, missingCount: 0 }
            }
            acc[key].missingCount += 1
            return acc
          }, {})

        const missingByPurchaseOrders = Object.values(missingByPOMap)

        return {
          productVariationID: first.productVariationID,
          description: first.description,
          expectedAmount,
          receivedAmount,
          missingAmount,
          purchaseOrderLineIDs: group.map(l => l.purchaseOrderLineID),
          missingByPurchaseOrders,
          poData
        }
      })
      .filter(x => x.receivedAmount !== x.expectedAmount)

    PurchaseOrderStoreActions.reportPurchaseOrderError(reportData)
  }

  onSaveClick = () => {
    const allLines = this.state.purchaseOrdersToReceiveDetails?.flatMap(po => po.purchaseOrderlines || []) || []
    const linesForWarehouse = allLines.filter(l => l.orderLineType === 'WAREHOUSE' && this.state.receivedAmounts[this.getGroupKey(l)] > 0)
    if (linesForWarehouse.length) {
      PurchaseOrderStoreActions.getStockLocationsForVariations(linesForWarehouse.map(l => l.productVariationID))
      PurchaseOrderStoreActions.getStockLocations()
      this.setState({ showStockLocationModal: true })
    } else {
      this.saveRecievedAmount()
    }
  }

  onSaveStockLocationModalClick = () => {
    this.setState({ showStockLocationModal: false })
    this.saveRecievedAmount()
  }

  render() {
    const { status } = this.state
    const isSaving = status === STATUS.IS_SAVING
    const isLoading = status === STATUS.IS_FETCHING
    const allLines = this.state.purchaseOrdersToReceiveDetails?.flatMap(po => po.purchaseOrderlines || []) || []
    const grouped = this.groupLinesByVariationAndDescription(allLines)
    const allComplete =
      grouped.length > 0 &&
      grouped.every(group => {
        const expectedAmount = group.reduce((sum, l) => sum + (l.amountOfProducts || 0), 0)
        const received = group.reduce((sum, l) => sum + (l.amountReceived || 0), 0)
        return received === expectedAmount && expectedAmount > 0
      })
    const linesForWarehouse = allLines.filter(l => l.orderLineType === 'WAREHOUSE' && this.state.receivedAmounts[this.getGroupKey(l)] > 0)
    return (
      <React.Fragment>
        <Box p={3} width='100%' maxWidth='100vw' mx='auto' position='relative'>
          <Backdrop open={isLoading || isSaving} style={{ zIndex: 1000, color: '#fff' }}>
            <CircularProgress color='inherit' />
          </Backdrop>
          <Typography variant='h5' gutterBottom style={{ fontWeight: 700, marginBottom: 24 }}>
            Modtag indkøbsordre {this.state.purchaseOrdersToReceiveDetails.map(po => po.purchaseOrderNumber).join(', ')}
          </Typography>
          <Divider style={{ marginBottom: 32 }} />
          <Paper
            elevation={3}
            style={{
              marginBottom: 32,
              padding: 0,
              borderRadius: 10,
              overflow: 'hidden',
              border: '1px solid #e0e0e0',
              background: '#fafbfc'
            }}
          >
            <Table size='small' style={{ minWidth: 900, width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: 120, fontWeight: 600, background: '#f6f8fa' }}>Varenr.</TableCell>
                  <TableCell style={{ width: 320, fontWeight: 600, background: '#f6f8fa' }}>Beskrivelse</TableCell>
                  <TableCell style={{ width: 80, fontWeight: 600, background: '#f6f8fa', textAlign: 'right' }}>Forventet antal</TableCell>
                  <TableCell style={{ width: 120, fontWeight: 600, background: '#f6f8fa', textAlign: 'right' }}>Modtaget</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {grouped.map(group => {
                  const first = group[0]
                  const groupKey = this.getGroupKey(first)
                  const expectedAmount = group.reduce((sum, l) => sum + (l.amountOfProducts || 0), 0)
                  const received = group.reduce((sum, l) => sum + (l.amountReceived || 0), 0)
                  const isComplete = received === expectedAmount && expectedAmount > 0
                  return (
                    <TableRow key={groupKey}>
                      <TableCell style={{ fontFamily: 'monospace' }}>{first.productVariationUID}</TableCell>
                      <TableCell>
                        <Typography variant='body2' style={{ whiteSpace: 'pre-line' }}>
                          {first.description}
                        </Typography>
                      </TableCell>
                      <TableCell style={{ textAlign: 'right' }}>{expectedAmount}</TableCell>
                      <TableCell style={{ textAlign: 'right' }}>
                        <TextField
                          variant='outlined'
                          size='small'
                          type='number'
                          inputProps={{ min: 0, style: { textAlign: 'right', width: 80 } }}
                          value={
                            this.state.receivedAmounts[groupKey] === 0 || this.state.receivedAmounts[groupKey] === null
                              ? ''
                              : this.state.receivedAmounts[groupKey]
                          }
                          onChange={e => this.handleReceivedAmountChange(groupKey, e.target.value)}
                          placeholder='0'
                          disabled={isSaving || isComplete}
                        />
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </Paper>

          <Box display='flex' justifyContent='flex-end' mt={4}>
            <button
              style={{
                padding: '10px 32px',
                background: 'red',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontWeight: 600,
                fontSize: 16,
                cursor: allComplete ? 'not-allowed' : 'pointer',
                opacity: isSaving ? 0.7 : 1,
                marginRight: 16
              }}
              onClick={this.reportError}
              disabled={isSaving || allComplete}
            >
              Rapportér fejl
            </button>
            <button
              style={{
                padding: '10px 32px',
                background: '#388e3c',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontWeight: 600,
                fontSize: 16,
                cursor: allComplete ? 'not-allowed' : 'pointer',
                opacity: isSaving ? 0.7 : 1,
                marginRight: 16
              }}
              onClick={this.onSaveClick}
              disabled={isSaving || allComplete}
            >
              Gem
            </button>
            <button
              style={{
                padding: '10px 32px',
                background: '#388e3c',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontWeight: 600,
                fontSize: 16
              }}
              onClick={this.openReceiveHandle}
              disabled={isSaving}
            >
              Håndter
            </button>
          </Box>

          <Box display={'flex'} flexDirection={'column'} gridGap={24} marginTop={6}>
            {this.state.purchaseOrdersToReceiveDetails.map(po => (
              <Paper key={po.PurchaseOrderID}>
                <Box display={'flex'} flexDirection={'column'} gridGap={2} padding={4}>
                  <Typography variant='h6' gutterBottom style={{ fontWeight: 700 }}>
                    {`Kommentarer for PO Nummer ${po.purchaseOrderNumber}`}
                  </Typography>
                  <CommentSection relatedType='PurchaseOrder' relatedID={po.PurchaseOrderID} />
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>

        <Modal
          isOpen={this.state.showStockLocationModal}
          toggle={() => this.setState({ showStockLocationModal: !this.state.showStockLocationModal })}
          size='lg'
        >
          <ModalHeader>
            <Typography variant='h6' style={{ fontWeight: 700 }}>
              Indtast lagerplacering for produkter bestilt til lager
            </Typography>
          </ModalHeader>
          <ModalBody>
            <Box display={'flex'} flexDirection={'column'} gridGap={12}>
              {this.state.productStockLocations.map((psl, i) => (
                <Box
                  key={psl.ProductID}
                  display={'flex'}
                  alignItems={'center'}
                  gridGap={40}
                  paddingBottom={2}
                  justifyContent={'space-between'}
                  borderBottom={i < this.state.productStockLocations.length - 1 ? '1px solid #efefef' : undefined}
                >
                  <Box display={'flex'} flexDirection={'column'} width={500}>
                    {linesForWarehouse
                      .filter(l => psl.variationIds.includes(l.productVariationID))
                      .reduce((receivedVariationsForWarehouse, curLine) => {
                        const existing = receivedVariationsForWarehouse.find(x => x.description === curLine.description)
                        if (existing) {
                          if (existing.amount < this.state.receivedAmounts[this.getGroupKey(curLine)]) {
                            existing.amount++
                          }
                        } else {
                          receivedVariationsForWarehouse.push({ description: curLine.description, amount: 1 })
                        }
                        return receivedVariationsForWarehouse
                      }, [])
                      .map(l => (
                        <Typography key={l.description}>
                          {l.amount}x {l.description}
                        </Typography>
                      ))}
                  </Box>
                  <Select
                    value={psl.StockLocationID || ''}
                    onChange={v => {
                      PurchaseOrderStoreActions.setProductStockLocation(psl.ProductID, v.target.value)
                    }}
                    style={{ width: 180, height: 40 }}
                    error={!psl.StockLocationID}
                    variant='outlined'
                  >
                    {this.state.stockLocations.map(sl => (
                      <MenuItem key={sl.StockLocationID} value={sl.StockLocationID}>
                        {sl.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              ))}
            </Box>
          </ModalBody>

          <ModalFooter>
            <Button onClick={this.onSaveStockLocationModalClick} disabled={this.state.productStockLocations.some(psl => !psl.StockLocationID)}>
              Ok
            </Button>
          </ModalFooter>
        </Modal>
      </React.Fragment>
    )
  }
}
